import React, { useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { drawRoom, getWallCoordinates } from '@/utils/roomDrawingFunctions';

const RoomPreview = ({ roomType, isSelected }) => {
    const canvasRef = useRef(null);
    const size = 100; // Size of the preview canvas

    // Sample wall data for each room type
    const sampleWalls = {
        'u-shaped': {
            back: 60,
            right: 60,
            left: 60,
        },
        'l-corner': {
            back: 60,
            left: 60,
        },
        'r-corner': {
            back: 60,
            right: 60,
        },
        'l-shaped': {
            back: 60,
            lRight: 30,
            lFrontR: 30,
            lShortSide: 30,
            lFrontL: 30,
            left: 60,
        },
        rectangular: {
            back: 60,
            right: 60,
            front: 60,
            left: 60,
        },
        rightOpenRectangular: {
            back: 60,
            right: 60,
            openFront: 30,
            left: 60,
        },
        leftOpenRectangular: {
            back: 60,
            right: 60,
            left: 60,
            openFront: 30,
        }
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, size, size);

        // Get wall coordinates
        const walls = sampleWalls[roomType];
        const coordinates = getWallCoordinates(walls, roomType);

        // Calculate scale and offset to fit the preview
        const maxX = Math.max(...Object.values(coordinates).map(coord => Math.max(coord.x1, coord.x2)));
        const maxY = Math.max(...Object.values(coordinates).map(coord => Math.max(coord.y1, coord.y2)));

        const padding = 20;
        const scaleX = (size - padding * 2) / maxX;
        const scaleY = (size - padding * 2) / maxY;
        const scale = Math.min(scaleX, scaleY);

        // Center the drawing
        const startX = (size - maxX * scale) / 2;
        const startY = (size - maxY * scale) / 2;

        // Draw the room
        drawRoom(ctx, walls, coordinates, {}, startX, startY, scale, 'in', false);
    }, [roomType, isSelected]);

    return <canvas ref={canvasRef} width={size} height={size} className="w-full h-full" />;
};

const RoomTypeSelector = ({ selectedType, onSelect }) => {
    const roomTypes = [
        { id: 'u-shaped', name: 'U-shaped Room' },
        { id: 'l-corner', name: 'Left Corner Room' },
        { id: 'r-corner', name: 'Right Corner Room' },
        { id: 'l-shaped', name: 'Special Shaped Room' },
        { id: 'rectangular', name: 'Rectangular Room' },
        { id: 'rightOpenRectangular', name: 'Right Open Rectangular' },
        { id: 'leftOpenRectangular', name: 'Left Open Rectangular' }    
    ];

    return (
        <div className="w-full p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {roomTypes.map(type => (
                    <Card
                        key={type.id}
                        className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                            selectedType === type.id ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => onSelect(type.id)}
                    >
                        <div className="p-4">
                            <div className="aspect-square mb-2 text-gray-600">
                                <RoomPreview roomType={type.id} isSelected={selectedType === type.id} />
                            </div>
                            <p className="text-sm text-center font-medium">{type.name}</p>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default RoomTypeSelector;
